import { hooks } from 'lib_ui-primitives';
import { constants, globalConfig } from 'lib_ui-services';

const { SCOPE, VISIBILITY, DURATION } = constants.retention;

const _p = {
    useBbState: hooks.useBbState,
    globalConfig
};
export const _private = _p;

// `continuousScan` is a per-tenant setting persisted into BbState by loadTenantSettings (see
// lib_ui-primitives/src/BbState). It must be read through useBbState rather than the
// globalConfig() snapshot: globalConfig._tenantSettings is a plain module variable that does not
// survive a JS reload and is only repopulated once the sync pipeline reaches
// didUpdate_application_useCase_syncAllDataToLocal. So on every cold start there is a window -
// measured at 27s on a TC22 - where globalConfig().tenantSettings is still `undefined` and the
// setting reads as false even though BbState already has the real value on disk. A component that
// sampled it in that window latched the legacy screen for the life of the mount, because
// setTenantSettings has no subscribers and nothing re-renders when the settings land.
const TENANT_SETTING_RETENTION = {
    scope: SCOPE.GLOBAL,
    visibility: VISIBILITY.PER_TENANT,
    duration: DURATION.ALWAYS
};

/**
 * Reactive equivalent of continuousScanSettings.isContinuousScanEnabled() for use inside
 * components. Same precedence - tenant setting > feature flag > false - but re-renders when the
 * tenant setting arrives post-sync instead of latching whatever was known at mount.
 *
 * The non-reactive function is still the right call outside React (e.g.
 * doingMark_item_item_multi.js), where there is no render to invalidate.
 * @returns {boolean} whether continuous scan is enabled for the current tenant
 */
export default function useContinuousScanEnabled() {
    const [tenantSetting] = _p.useBbState(
        _p.globalConfig().tenantSettings?.continuousScan ?? undefined,
        'continuousScan',
        TENANT_SETTING_RETENTION
    );

    // Debug override for testing, mirroring continuousScanSettings.isContinuousScanEnabled.
    // @ts-ignore
    if (typeof window !== 'undefined' && window.__FORCE_CONTINUOUS_SCAN__) return true;

    // Tenant setting wins whenever it has actually been answered either way, so a tenant that has
    // deliberately turned continuous scan off is not re-enabled by the feature flag below.
    if (tenantSetting !== undefined) return !!tenantSetting;

    return (_p.globalConfig().featureFlags || []).includes('continuousScan');
}
